import { API_URL } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Edit, Activity, ChevronLeft, ChevronRight, Users as UsersIcon, Archive, ArchiveRestore, Trash2, AlertTriangle, Eye, EyeOff, Lock, Mail, Coins, History, ChevronDown, ChevronUp } from 'lucide-react';
import AddPromoCreditsModal from '../components/AddPromoCreditsModal';
import ComposeEmailModal from '../components/modals/ComposeEmailModal';
import axios from 'axios';
import { useNotification } from '@/contexts/NotificationContext';
import AdminNav from '../components/AdminNav';
import AdminTopMenuBar from '../components/AdminTopMenuBar';
import AdminMobileBottomNav from '../components/AdminMobileBottomNav';
import UniversalFooter from '../components/UniversalFooter';

// API_URL imported from centralized config
const API = API_URL;

/**
 * Safe error message extraction utility
 * Prevents React error #31 by ensuring we never render an object directly
 * @param {any} error - The error from axios catch block
 * @returns {string} - A safe string message to display
 */
const formatErrorMessage = (error) => {
  // If it's already a string, return as-is
  if (typeof error === 'string') {
    return error;
  }
  
  // Handle axios error response
  if (error?.response?.data) {
    const data = error.response.data;
    
    // Handle our custom error shape: { error: { message: "..." } }
    if (data?.error?.message) {
      return String(data.error.message);
    }
    
    // Handle FastAPI 422 validation error shape
    if (data?.detail) {
      // If detail is an array (validation errors)
      if (Array.isArray(data.detail)) {
        const firstError = data.detail[0];
        if (firstError?.msg) {
          const field = firstError.loc?.join('.') || 'field';
          return `Validation error: ${firstError.msg} (${field})`;
        }
        return 'Validation error in request';
      }
      // If detail is a string
      if (typeof data.detail === 'string') {
        return data.detail;
      }
      // If detail is an object, stringify it safely
      if (typeof data.detail === 'object') {
        try {
          return JSON.stringify(data.detail).slice(0, 200);
        } catch {
          return 'Request failed with validation error';
        }
      }
    }
    
    // Handle plain message field
    if (data?.message && typeof data.message === 'string') {
      return data.message;
    }
  }
  
  // Handle error with message property
  if (error?.message && typeof error.message === 'string') {
    return error.message;
  }
  
  // Last resort: try to stringify but truncate
  try {
    const str = JSON.stringify(error);
    return str.length > 100 ? str.slice(0, 100) + '...' : str;
  } catch {
    return 'An unexpected error occurred';
  }
};

const AdminUserManagementPage = () => {
  const { user: currentUser, logout, getHomePage } = useAuth();
  const navigate = useNavigate();
  
  const handleLogout = () => {
    logout();
    navigate("/");
  };
  const { notifyError, notifySuccess } = useNotification();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [promoCreditsModalOpen, setPromoCreditsModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState({ email: '', name: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [savingUser, setSavingUser] = useState(false);  // Track save in progress
  
  // Promo credit history state
  const [promoHistory, setPromoHistory] = useState(null);
  const [promoHistoryExpanded, setPromoHistoryExpanded] = useState(false);
  const [loadingPromoHistory, setLoadingPromoHistory] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    alternate_email: '',
    city: '',
    state: '',
    country: '',
    roles: [],
    new_password: ''  // For password reset
  });
  
  // Password visibility toggle for edit modal
  const [showEditPassword, setShowEditPassword] = useState(false);

  // Create user form state
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    alternate_email: '',
    city: '',
    state: '',
    country: '',
    roles: ['reader']
  });

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        console.error('No access token found');
        notifyError('Not authenticated. Please log in again.');
        setLoading(false);
        return;
      }
      
      const params = {
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_dir: sortDir
      };
      
      if (searchQuery.trim()) {
        params.q = searchQuery.trim();
      }
      
      const response = await axios.get(`${API}/api/admin/users`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let filteredUsers = response.data.items || [];
      
      // Apply role filter (client-side)
      if (roleFilter) {
        filteredUsers = filteredUsers.filter(user => 
          user.roles && user.roles.includes(roleFilter)
        );
      }
      
      // Apply status filter (client-side)
      if (statusFilter === 'active') {
        filteredUsers = filteredUsers.filter(user => !user.archived);
      } else if (statusFilter === 'archived') {
        filteredUsers = filteredUsers.filter(user => user.archived);
      }
      
      setUsers(filteredUsers);
      setTotalUsers(filteredUsers.length);
    } catch (error) {
      console.error('Error fetching users:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMsg = formatErrorMessage(error);
      notifyError(errorMsg);
      
      // Set empty state on error
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1); // Reset to first page on new search
      fetchUsers();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch on page, sort, pageSize, or filter change
  useEffect(() => {
    fetchUsers();
  }, [page, sortBy, sortDir, pageSize, roleFilter, statusFilter]);

  // Handle sort change
  const handleSortChange = (value) => {
    const sortMap = {
      'newest': { sort_by: 'created_at', sort_dir: 'desc' },
      'oldest': { sort_by: 'created_at', sort_dir: 'asc' },
      'email_asc': { sort_by: 'email', sort_dir: 'asc' },
      'name_asc': { sort_by: 'first_name', sort_dir: 'asc' },
      'last_login': { sort_by: 'last_login_at', sort_dir: 'desc' }
    };
    
    const sort = sortMap[value];
    if (sort) {
      setSortBy(sort.sort_by);
      setSortDir(sort.sort_dir);
      setPage(1);
    }
  };

  // Open edit modal
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      alternate_email: user.alternate_email || '',
      city: user.city || '',
      state: user.state || '',
      country: user.country || '',
      roles: user.roles || ['reader'],
      new_password: ''  // Reset password field when opening modal
    });
    setShowEditPassword(false);  // Reset password visibility
    setEditModalOpen(true);
  };

  // Save user edits
  const handleSaveUser = async () => {
    if (savingUser) return; // Prevent double-click
    
    setSavingUser(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        notifyError('Session expired. Please log in again.');
        setSavingUser(false);
        return;
      }
      
      // Build the update payload, excluding empty new_password
      const payload = {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        phone: editForm.phone,
        alternate_email: editForm.alternate_email,
        city: editForm.city,
        state: editForm.state,
        country: editForm.country,
        roles: editForm.roles
      };
      
      // Only include password if it's not empty
      if (editForm.new_password && editForm.new_password.trim() !== '') {
        payload.new_password = editForm.new_password.trim();
      }
      
      const response = await axios.put(
        `${API}/api/admin/users/${selectedUser.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('[SUCCESS] User updated successfully', response.data);
      
      // Update the users list locally first for immediate feedback
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === selectedUser.id 
            ? { ...u, ...payload, new_password: undefined }
            : u
        )
      );
      
      // Close modal and clear state
      setEditModalOpen(false);
      setSelectedUser(null);
      setEditForm({
        first_name: '',
        last_name: '',
        phone: '',
        alternate_email: '',
        city: '',
        state: '',
        country: '',
        roles: [],
        new_password: ''
      });
      
      // Show success notification
      notifySuccess('User updated successfully');
      
      // Refresh the full list in background (non-blocking)
      fetchUsers().catch(err => {
        console.error('Error refreshing user list:', err);
      });
      
    } catch (error) {
      console.error('Error updating user:', error);
      const errorMsg = formatErrorMessage(error);
      notifyError(errorMsg);
      // Keep modal open on error so user can retry
    } finally {
      setSavingUser(false);
    }
  };

  // Open activity modal
  const handleViewActivity = async (user) => {
    setSelectedUser(user);
    setActivityModalOpen(true);
    setPromoHistory(null);  // Reset promo history when opening
    setPromoHistoryExpanded(false);  // Collapse history section
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API}/api/admin/users/${user.id}/activity`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setActivityData(response.data);
    } catch (error) {
      console.error('Error fetching activity:', error);
      notifyError('Failed to load user activity');
      setActivityData(null);
    }
  };

  // Fetch promo credit history
  const fetchPromoHistory = async (userId) => {
    if (promoHistory) return; // Already loaded
    
    setLoadingPromoHistory(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API}/api/admin/users/${userId}/promo-credits/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPromoHistory(response.data);
    } catch (error) {
      console.error('Error fetching promo history:', error);
      notifyError('Failed to load promotional credit history');
    } finally {
      setLoadingPromoHistory(false);
    }
  };

  // Toggle promo history expansion
  const togglePromoHistory = () => {
    const newExpanded = !promoHistoryExpanded;
    setPromoHistoryExpanded(newExpanded);
    
    // Fetch history when expanding for the first time
    if (newExpanded && !promoHistory && selectedUser) {
      fetchPromoHistory(selectedUser.id);
    }
  };

  // Toggle role (for edit modal)
  const toggleRole = (role) => {
    const currentRoles = editForm.roles;
    
    // Cannot remove 'reader' role unless user is banned
    if (role === 'reader' && currentRoles.includes('reader') && !currentRoles.includes('banned')) {
      console.log('[WARNING] ' + 'Cannot remove reader role unless user is banned');
      return;
    }
    
    // Cannot remove own admin role
    if (role === 'admin' && selectedUser.id === currentUser.id && currentRoles.includes('admin')) {
      console.log('[WARNING] ' + 'Cannot remove your own admin role');
      return;
    }
    
    if (currentRoles.includes(role)) {
      // Removing a role
      setEditForm({
        ...editForm,
        roles: currentRoles.filter(r => r !== role)
      });
    } else {
      // Adding a role - enforce Author/Publisher mutual exclusivity
      let newRoles = [...currentRoles, role];
      
      // Publisher and Author (creator) are mutually exclusive
      if (role === 'publisher' && newRoles.includes('creator')) {
        // Remove 'creator' when adding 'publisher'
        newRoles = newRoles.filter(r => r !== 'creator');
        console.log('[INFO] Removed Author role - Publisher and Author are mutually exclusive');
      } else if (role === 'creator' && newRoles.includes('publisher')) {
        // Remove 'publisher' when adding 'creator'
        newRoles = newRoles.filter(r => r !== 'publisher');
        console.log('[INFO] Removed Publisher role - Publisher and Author are mutually exclusive');
      }
      
      setEditForm({
        ...editForm,
        roles: newRoles
      });
    }
  };

  // Toggle role (for create modal)
  const toggleCreateRole = (role) => {
    const currentRoles = createForm.roles;
    
    if (currentRoles.includes(role)) {
      // Prevent removing all roles
      if (currentRoles.length === 1) {
        console.log('[WARNING] ' + 'User must have at least one role');
        return;
      }
      setCreateForm({
        ...createForm,
        roles: currentRoles.filter(r => r !== role)
      });
    } else {
      // Adding a role - enforce Author/Publisher mutual exclusivity
      let newRoles = [...currentRoles, role];
      
      // Publisher and Author (creator) are mutually exclusive
      if (role === 'publisher' && newRoles.includes('creator')) {
        newRoles = newRoles.filter(r => r !== 'creator');
        console.log('[INFO] Removed Author role - Publisher and Author are mutually exclusive');
      } else if (role === 'creator' && newRoles.includes('publisher')) {
        newRoles = newRoles.filter(r => r !== 'publisher');
        console.log('[INFO] Removed Publisher role - Publisher and Author are mutually exclusive');
      }
      
      setCreateForm({
        ...createForm,
        roles: newRoles
      });
    }
  };

  // Handle archive user
  const handleArchiveUser = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      await axios.patch(
        `${API}/api/admin/users/${selectedUser.id}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('[SUCCESS] ' + `User ${selectedUser.email} has been archived`);
      setArchiveModalOpen(false);
      setSelectedUser(null);
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error archiving user:', error);
      const errorMsg = formatErrorMessage(error);
      notifyError(errorMsg);
    }
  };

  // Handle unarchive user
  const handleUnarchiveUser = async (user) => {
    try {
      const token = localStorage.getItem('accessToken');
      
      await axios.patch(
        `${API}/api/admin/users/${user.id}/unarchive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('[SUCCESS] ' + `User ${user.email} has been unarchived`);
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error unarchiving user:', error);
      const errorMsg = formatErrorMessage(error);
      notifyError(errorMsg);
    }
  };

  // Handle delete user
  const handleDeleteUser = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      await axios.delete(
        `${API}/api/admin/users/${selectedUser.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('[SUCCESS] ' + `User ${selectedUser.email} and all associated data have been permanently deleted`);
      setDeleteModalOpen(false);
      setDeleteStep(1);
      setDeleteConfirmEmail('');
      setSelectedUser(null);
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMsg = formatErrorMessage(error);
      notifyError(errorMsg);
    }
  };

  // Handle create user
  const handleCreateUser = async () => {
    try {
      // Validate required fields
      if (!createForm.email || !createForm.password || !createForm.first_name || !createForm.last_name) {
        notifyError('Please fill in all required fields');
        return;
      }

      const token = localStorage.getItem('accessToken');
      
      const response = await axios.post(
        `${API}/api/admin/users/create`,
        createForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('[SUCCESS] ' + `User ${createForm.email} created successfully`);
      setCreateModalOpen(false);
      
      // Reset form
      setCreateForm({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        alternate_email: '',
        city: '',
        state: '',
        country: '',
        roles: ['reader']
      });
      
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMsg = formatErrorMessage(error);
      notifyError(errorMsg);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Role badge component
  const RoleBadge = ({ role }) => {
    const colors = {
      reader: 'bg-slate-700/40 text-slate-200 ring-slate-500/40',
      creator: 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/40',
      publisher: 'bg-purple-500/15 text-purple-300 ring-purple-400/40',
      admin: 'bg-yellow-500/15 text-yellow-300 ring-yellow-400/40',
      beta_tester: 'bg-cyan-500/15 text-cyan-300 ring-cyan-400/40',
      promotional: 'bg-pink-500/15 text-pink-300 ring-pink-400/40',
      banned: 'bg-red-500/20 text-red-300 ring-red-400/50'
    };
    
    const labels = {
      reader: 'Reader',
      creator: 'Author',
      publisher: 'Publisher',
      admin: 'Admin',
      beta_tester: 'Beta Tester',
      promotional: 'Promotional',
      banned: 'Banned'
    };
    
    return (
      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${colors[role] || colors.reader}`}>
        {labels[role] || role}
      </span>
    );
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalUsers / pageSize);
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalUsers);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      {/* Admin Navigation */}
      <AdminNav />
      <AdminTopMenuBar />
      
      {/* Main Content with mobile bottom padding */}
      <div className="pb-20 md:pb-0">
        {/* Header */}
        <header className="bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-b border-slate-800">
          <div className="mx-auto max-w-7xl px-4 py-10 sm:py-12">
            <div className="flex items-center gap-3 mb-3">
              <UsersIcon className="w-8 h-8 text-yellow-400" />
              <h1 className="text-3xl sm:text-4xl font-bold">User Management</h1>
            </div>
            <p className="text-sm sm:text-base text-slate-300 max-w-3xl">
              Search, filter, and manage all NewAudia<sup className="font-bold text-[0.6em]">TM</sup> users from a single dashboard. 
              Monitor user activity, update profiles, and manage roles.
            </p>
          </div>
        </header>

        {/* Main Content */}
        <main className="mx-auto max-w-7xl px-4 py-8">
        {/* Controls Toolbar */}
        <div className="mb-6 space-y-4">
          {/* Row 1: Search and Create Button */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by name, email, or city..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-900/60 border-slate-800 text-slate-100 placeholder:text-slate-400"
              />
            </div>

            {/* Create New User Button */}
            <Button
              onClick={() => setCreateModalOpen(true)}
              className="bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700 text-white font-semibold shadow-lg"
            >
              <UsersIcon className="w-4 h-4 mr-2" />
              Create New User
            </Button>
          </div>

          {/* Row 2: Filters and Sort */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Role Filter */}
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 bg-slate-900/60 border border-slate-800 rounded-md text-sm text-slate-100"
            >
              <option value="">All Roles</option>
              <option value="reader">Reader</option>
              <option value="creator">Author</option>
              <option value="publisher">Publisher</option>
              <option value="admin">Admin</option>
              <option value="beta_tester">Beta Tester</option>
              <option value="promotional">Promotional</option>
              <option value="banned">Banned</option>
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 bg-slate-900/60 border border-slate-800 rounded-md text-sm text-slate-100"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>

            {/* Sort Dropdown */}
            <select
              value={`${sortBy}_${sortDir}`}
              onChange={(e) => handleSortChange(e.target.value)}
              className="px-4 py-2 bg-slate-900/60 border border-slate-800 rounded-md text-sm text-slate-100"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="email_asc">Email A→Z</option>
              <option value="name_asc">Name A→Z</option>
              <option value="last_login">Last Login (Recent)</option>
            </select>

            {/* Page Size */}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="px-4 py-2 bg-slate-900/60 border border-slate-800 rounded-md text-sm text-slate-100"
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
        </div>

        {/* User Table */}
        <div className="rounded-3xl bg-slate-900/60 shadow-xl shadow-slate-950/50 ring-1 ring-slate-800 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-indigo-500 border-r-transparent border-b-purple-500 border-l-transparent"></div>
              <p className="mt-4 text-sm text-slate-300">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <UsersIcon className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-lg font-medium text-slate-300">No users found</p>
              <p className="text-sm text-slate-400 mt-2">
                {searchQuery ? 'Try adjusting your search criteria' : 'No users in the system yet'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/80 border-b border-slate-700">
                    <tr className="text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Roles</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Location</th>
                      <th className="px-4 py-3">Created</th>
                      <th className="px-4 py-3">Last Login</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {users.map((user) => (
                      <tr key={user.id} className={`transition-colors ${user.archived ? 'bg-slate-800/20 opacity-60' : 'hover:bg-slate-800/40'}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-100">
                            {user.first_name && user.last_name 
                              ? `${user.first_name} ${user.last_name}`
                              : user.name || 'N/A'
                            }
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">
                          <div className="flex items-center gap-2">
                            <span>{user.email}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEmailRecipient({
                                  email: user.email,
                                  name: user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : ''
                                });
                                setEmailModalOpen(true);
                              }}
                              className="inline-flex items-center justify-center p-1 rounded-full text-slate-400 hover:text-cyan-400 hover:bg-cyan-900/30 transition-colors"
                              title={`Send email to ${user.email} from info@newaudia.com`}
                              data-testid={`email-user-${user.id}`}
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(user.roles || ['reader']).map(role => (
                              <RoleBadge key={role} role={role} />
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {user.archived ? (
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-orange-500/15 text-orange-300 ring-1 ring-orange-400/40">
                              <Archive className="w-3 h-3" />
                              Archived
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-green-500/15 text-green-300 ring-1 ring-green-400/40">
                              Active
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">
                          {[user.city, user.state, user.country].filter(Boolean).join(', ') || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">
                          {formatDate(user.last_login_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2 flex-wrap">
                            <button
                              onClick={() => navigate(`/admin/users/${user.id}`)}
                              className="inline-flex items-center gap-1 rounded-full bg-cyan-600/20 px-3 py-1 text-xs font-semibold text-cyan-300 hover:bg-cyan-600/30 transition-colors"
                              data-testid={`view-user-${user.id}`}
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </button>
                            <button
                              onClick={() => handleEditUser(user)}
                              className="inline-flex items-center gap-1 rounded-full bg-indigo-600/20 px-3 py-1 text-xs font-semibold text-indigo-300 hover:bg-indigo-600/30 transition-colors"
                            >
                              <Edit className="w-3 h-3" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleViewActivity(user)}
                              className="inline-flex items-center gap-1 rounded-full bg-emerald-600/20 px-3 py-1 text-xs font-semibold text-emerald-300 hover:bg-emerald-600/30 transition-colors"
                            >
                              <Activity className="w-3 h-3" />
                              Activity
                            </button>
                            {user.archived ? (
                              <button
                                onClick={() => handleUnarchiveUser(user)}
                                className="inline-flex items-center gap-1 rounded-full bg-blue-600/20 px-3 py-1 text-xs font-semibold text-blue-300 hover:bg-blue-600/30 transition-colors"
                              >
                                <ArchiveRestore className="w-3 h-3" />
                                Unarchive
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  setSelectedUser(user);
                                  setArchiveModalOpen(true);
                                }}
                                className="inline-flex items-center gap-1 rounded-full bg-orange-600/20 px-3 py-1 text-xs font-semibold text-orange-300 hover:bg-orange-600/30 transition-colors"
                              >
                                <Archive className="w-3 h-3" />
                                Archive
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setDeleteStep(1);
                                setDeleteConfirmEmail('');
                                setDeleteModalOpen(true);
                              }}
                              className="inline-flex items-center gap-1 rounded-full bg-red-600/20 px-3 py-1 text-xs font-semibold text-red-300 hover:bg-red-600/30 transition-colors"
                            >
                              <Trash2 className="w-3 h-3" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-4 py-4 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-slate-400">
                  Showing <span className="font-medium text-slate-200">{startItem}–{endItem}</span> of{' '}
                  <span className="font-medium text-slate-200">{totalUsers}</span> users
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    variant="outline"
                    size="sm"
                    className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-slate-300">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    variant="outline"
                    size="sm"
                    className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onOpenChange={(open) => {
        // Prevent closing while save is in progress
        if (!savingUser) {
          setEditModalOpen(open);
          if (!open) {
            setSelectedUser(null);
          }
        }
      }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Edit User</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update user profile and role information
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4 mt-4">
              {/* Read-only fields */}
              <div className="p-3 bg-slate-950/50 rounded-lg border border-slate-800">
                <label className="text-xs text-slate-400">Primary Email (Read-only)</label>
                <p className="text-sm text-slate-300 font-medium mt-1">{selectedUser.email}</p>
              </div>

              <div className="p-3 bg-slate-950/50 rounded-lg border border-slate-800">
                <label className="text-xs text-slate-400">User ID</label>
                <code
                  data-testid="user-id-copyable"
                  className="block text-xs text-amber-400 bg-slate-800 px-2 py-1 rounded cursor-pointer hover:bg-slate-700 transition-colors font-mono select-all mt-1"
                  title="Click to copy"
                  onClick={(e) => {
                    navigator.clipboard.writeText(selectedUser.id);
                    e.target.classList.add('ring-1', 'ring-amber-400');
                    setTimeout(() => e.target.classList.remove('ring-1', 'ring-amber-400'), 800);
                  }}
                >
                  {selectedUser.id}
                </code>
              </div>

              {/* Password Field - Admin can view and edit */}
              <div className="p-3 bg-slate-950/50 rounded-lg border border-slate-800">
                <label className="text-sm font-medium text-slate-300 mb-2 block">
                  <Lock className="inline w-4 h-4 mr-1" />
                  Set New Password
                </label>
                <div className="relative">
                  <Input
                    type={showEditPassword ? "text" : "password"}
                    value={editForm.new_password}
                    onChange={(e) => setEditForm({...editForm, new_password: e.target.value})}
                    placeholder="Enter new password (leave blank to keep current)"
                    className="bg-slate-950/50 border-slate-700 text-slate-100 pr-10"
                    data-testid="edit-user-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEditPassword(!showEditPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-200 focus:outline-none"
                    data-testid="edit-user-toggle-password"
                  >
                    {showEditPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Leave blank to keep the user's current password. Click the eye icon to view/hide.
                </p>
              </div>

              {/* Editable fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">First Name</label>
                  <Input
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                    className="bg-slate-950/50 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">Last Name</label>
                  <Input
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                    className="bg-slate-950/50 border-slate-700 text-slate-100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">Phone</label>
                  <Input
                    value={editForm.phone}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    className="bg-slate-950/50 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">Alternate Email</label>
                  <Input
                    type="email"
                    value={editForm.alternate_email}
                    onChange={(e) => setEditForm({...editForm, alternate_email: e.target.value})}
                    className="bg-slate-950/50 border-slate-700 text-slate-100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">City</label>
                  <Input
                    value={editForm.city}
                    onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                    className="bg-slate-950/50 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">State</label>
                  <Input
                    value={editForm.state}
                    onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                    className="bg-slate-950/50 border-slate-700 text-slate-100"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">Country</label>
                  <Input
                    value={editForm.country}
                    onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                    className="bg-slate-950/50 border-slate-700 text-slate-100"
                  />
                </div>
              </div>

              {/* Roles */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">Roles</label>
                <div className="flex flex-wrap gap-2">
                  {['reader', 'creator', 'publisher', 'admin', 'beta_tester', 'promotional', 'banned'].map(role => {
                    const roleLabels = {
                      reader: 'Reader',
                      creator: 'Author',
                      publisher: 'Publisher',
                      admin: 'Admin',
                      beta_tester: 'Beta Tester',
                      promotional: 'Promotional',
                      banned: 'Banned'
                    };
                    
                    return (
                      <button
                        key={role}
                        onClick={() => toggleRole(role)}
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                          editForm.roles.includes(role)
                            ? role === 'banned' 
                              ? 'bg-red-600 text-white'
                              : 'bg-indigo-600 text-white'
                            : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                        }`}
                      >
                        {roleLabels[role]}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Note: All users must have the 'reader' role unless banned. <span className="text-amber-400">Publisher and Author roles are mutually exclusive.</span> You cannot remove your own 'admin' role.
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  onClick={() => setEditModalOpen(false)}
                  variant="outline"
                  className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
                  disabled={savingUser}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveUser}
                  className="bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                  disabled={savingUser}
                >
                  {savingUser ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Create User Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Create New User</DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new user account with custom roles and profile information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Email and Password */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-300 mb-1 block">
                  Email <span className="text-red-400">*</span>
                </label>
                <Input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                  placeholder="user@example.com"
                  className="bg-slate-950/50 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300 mb-1 block">
                  Password <span className="text-red-400">*</span>
                </label>
                <Input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                  placeholder="Secure password"
                  className="bg-slate-950/50 border-slate-700 text-slate-100"
                />
              </div>
            </div>

            {/* Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-300 mb-1 block">
                  First Name <span className="text-red-400">*</span>
                </label>
                <Input
                  value={createForm.first_name}
                  onChange={(e) => setCreateForm({...createForm, first_name: e.target.value})}
                  className="bg-slate-950/50 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300 mb-1 block">
                  Last Name <span className="text-red-400">*</span>
                </label>
                <Input
                  value={createForm.last_name}
                  onChange={(e) => setCreateForm({...createForm, last_name: e.target.value})}
                  className="bg-slate-950/50 border-slate-700 text-slate-100"
                />
              </div>
            </div>

            {/* Phone and Alternate Email */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-300 mb-1 block">Phone</label>
                <Input
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({...createForm, phone: e.target.value})}
                  placeholder="(optional)"
                  className="bg-slate-950/50 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300 mb-1 block">Alternate Email</label>
                <Input
                  type="email"
                  value={createForm.alternate_email}
                  onChange={(e) => setCreateForm({...createForm, alternate_email: e.target.value})}
                  placeholder="(optional)"
                  className="bg-slate-950/50 border-slate-700 text-slate-100"
                />
              </div>
            </div>

            {/* Location */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-300 mb-1 block">City</label>
                <Input
                  value={createForm.city}
                  onChange={(e) => setCreateForm({...createForm, city: e.target.value})}
                  className="bg-slate-950/50 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300 mb-1 block">State</label>
                <Input
                  value={createForm.state}
                  onChange={(e) => setCreateForm({...createForm, state: e.target.value})}
                  className="bg-slate-950/50 border-slate-700 text-slate-100"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-300 mb-1 block">Country</label>
                <Input
                  value={createForm.country}
                  onChange={(e) => setCreateForm({...createForm, country: e.target.value})}
                  className="bg-slate-950/50 border-slate-700 text-slate-100"
                />
              </div>
            </div>

            {/* Roles */}
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">
                Roles <span className="text-red-400">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {['reader', 'creator', 'publisher', 'admin', 'beta_tester', 'promotional', 'banned'].map(role => {
                  const roleLabels = {
                    reader: 'Reader',
                    creator: 'Author',
                    publisher: 'Publisher',
                    admin: 'Admin',
                    beta_tester: 'Beta Tester',
                    promotional: 'Promotional',
                    banned: 'Banned'
                  };
                  
                  return (
                    <button
                      key={role}
                      onClick={() => toggleCreateRole(role)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                        createForm.roles.includes(role)
                          ? role === 'banned' 
                            ? 'bg-red-600 text-white'
                            : 'bg-indigo-600 text-white'
                          : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                      }`}
                    >
                      {roleLabels[role]}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Select one or more roles. Users must have at least one role.
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                onClick={() => {
                  setCreateModalOpen(false);
                  // Reset form
                  setCreateForm({
                    email: '',
                    password: '',
                    first_name: '',
                    last_name: '',
                    phone: '',
                    alternate_email: '',
                    city: '',
                    state: '',
                    country: '',
                    roles: ['reader']
                  });
                }}
                variant="outline"
                className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateUser}
                className="bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700 text-white"
              >
                <UsersIcon className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Activity Modal */}
      <Dialog open={activityModalOpen} onOpenChange={setActivityModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-bold">User Activity</DialogTitle>
            <DialogDescription className="text-slate-400">
              Engagement metrics and usage summary
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4 mt-4 overflow-y-auto flex-1 pr-2">
              {/* User Info */}
              <div className="p-4 bg-slate-950/50 rounded-lg border border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-100">
                    {selectedUser.first_name && selectedUser.last_name 
                      ? `${selectedUser.first_name} ${selectedUser.last_name}`
                      : selectedUser.name || 'User'
                    }
                  </h3>
                  {/* Subscription Type Badge */}
                  {activityData && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      activityData.subscription_type === 'Monthly Subscriber' ? 'bg-green-900/50 text-green-400 border border-green-500/30' :
                      activityData.subscription_type === 'Annual Subscriber' ? 'bg-amber-900/50 text-amber-400 border border-amber-500/30' :
                      activityData.subscription_type === 'A la Carte' ? 'bg-blue-900/50 text-blue-400 border border-blue-500/30' :
                      activityData.subscription_type === 'Cancelled' ? 'bg-red-900/50 text-red-400 border border-red-500/30' :
                      'bg-slate-800 text-slate-400 border border-slate-700'
                    }`}>
                      {activityData.subscription_type}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-400 mb-2">{selectedUser.email}</p>
                <div className="flex flex-wrap gap-1">
                  {(selectedUser.roles || ['reader']).map(role => (
                    <RoleBadge key={role} role={role} />
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-400">Member since:</span>
                    <p className="font-medium text-slate-200">{formatDate(selectedUser.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Last login:</span>
                    <p className="font-medium text-slate-200">{formatDate(selectedUser.last_login_at)}</p>
                  </div>
                </div>
              </div>

              {/* Activity Metrics */}
              {activityData ? (
                <div className="space-y-4">
                  {/* Row 1: Sessions & Books */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-950/50 rounded-lg border border-slate-800">
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Total Sessions</p>
                      <p className="text-2xl font-bold text-slate-100">{activityData.total_sessions}</p>
                    </div>
                    <div className="p-4 bg-slate-950/50 rounded-lg border border-slate-800">
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Books in Library</p>
                      <p className="text-2xl font-bold text-slate-100">{activityData.total_books_in_library}</p>
                    </div>
                  </div>

                  {/* Row 2: Reader Credits Balance & Consumed */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-950/50 rounded-lg border border-emerald-500/30">
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Credits Balance</p>
                      <p className="text-2xl font-bold text-emerald-400">
                        {activityData.reader_credits_balance?.toFixed(2) || '0.00'} <span className="text-base font-normal text-slate-400">RC</span>
                      </p>
                    </div>
                    <div className="p-4 bg-slate-950/50 rounded-lg border border-slate-800">
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Credits Consumed</p>
                      <p className="text-2xl font-bold text-amber-400">
                        {activityData.reader_credits_consumed?.toFixed(2) || '0.00'} <span className="text-base font-normal text-slate-400">RC</span>
                      </p>
                    </div>
                  </div>

                  {/* Row 3: Avg Session & Total Sessions */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-950/50 rounded-lg border border-slate-800">
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Avg Session</p>
                      <p className="text-2xl font-bold text-slate-100">
                        {activityData.avg_session_length 
                          ? `${Math.round(activityData.avg_session_length / 60)} min`
                          : 'N/A'
                        }
                      </p>
                    </div>
                    <div className="p-4 bg-slate-950/50 rounded-lg border border-slate-800">
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Total Sessions</p>
                      <p className="text-2xl font-bold text-slate-100">{activityData.total_sessions}</p>
                    </div>
                  </div>

                  {/* Row 3: Ask Audia & Last Activity */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-950/50 rounded-lg border border-slate-800">
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Ask Audia</p>
                      <p className="text-2xl font-bold text-purple-400">
                        {activityData.ask_audia_questions} <span className="text-base font-normal text-slate-400">questions</span>
                      </p>
                    </div>
                    <div className="p-4 bg-slate-950/50 rounded-lg border border-slate-800">
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Last Activity</p>
                      <p className="text-lg font-medium text-slate-100">
                        {activityData.last_activity_at 
                          ? formatDate(activityData.last_activity_at)
                          : 'No activity yet'
                        }
                      </p>
                    </div>
                  </div>

                  {/* Top 3 Titles */}
                  {activityData.top_titles && activityData.top_titles.length > 0 && (
                    <div className="p-4 bg-slate-950/50 rounded-lg border border-slate-800">
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-3">Top 3 Titles</p>
                      <div className="space-y-2">
                        {activityData.top_titles.map((title, i) => (
                          <div key={i} className="flex justify-between items-center text-sm">
                            <span className="text-slate-300 truncate flex-1 mr-2">
                              <span className="text-slate-500 mr-2">{title.rank}.</span>
                              "{title.title}"
                            </span>
                            <span className="text-slate-400 whitespace-nowrap">{title.minutes} min</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Total Revenue */}
                  <div className="p-4 bg-gradient-to-r from-emerald-950/50 to-slate-950/50 rounded-lg border border-emerald-800/50">
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Total Revenue</p>
                    <p className="text-3xl font-bold text-emerald-400">
                      ${(activityData.total_revenue_cents / 100).toFixed(2)}
                    </p>
                  </div>

                  {/* Promotional Credit History - Collapsible */}
                  <div className="rounded-lg border border-amber-500/30 overflow-hidden">
                    <button
                      onClick={togglePromoHistory}
                      className="w-full p-4 bg-amber-950/30 flex items-center justify-between hover:bg-amber-950/50 transition-colors"
                      data-testid="promo-history-toggle"
                    >
                      <div className="flex items-center gap-2">
                        <History className="w-4 h-4 text-amber-400" />
                        <span className="text-sm font-medium text-amber-300">Promotional Credits History</span>
                        {promoHistory && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-amber-600/30 text-amber-300">
                            {promoHistory.award_count} awards · {promoHistory.total_promo_credits_awarded} RC
                          </span>
                        )}
                      </div>
                      {promoHistoryExpanded ? (
                        <ChevronUp className="w-4 h-4 text-amber-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-amber-400" />
                      )}
                    </button>
                    
                    {promoHistoryExpanded && (
                      <div className="p-4 bg-slate-950/50 border-t border-amber-500/20">
                        {loadingPromoHistory ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-t-amber-500 border-r-transparent border-b-amber-500 border-l-transparent"></div>
                            <span className="ml-2 text-sm text-slate-400">Loading history...</span>
                          </div>
                        ) : promoHistory && promoHistory.history.length > 0 ? (
                          <div className="space-y-3 max-h-48 overflow-y-auto">
                            {promoHistory.history.map((entry, index) => (
                              <div 
                                key={entry.id || index} 
                                className="p-3 bg-slate-900/50 rounded-lg border border-slate-800"
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-lg font-bold text-amber-400">+{entry.amount} RC</span>
                                  <span className="text-xs text-slate-500">
                                    {entry.awarded_at ? new Date(entry.awarded_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    }) : 'Unknown date'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300">{entry.reason}</span>
                                  <span className="text-slate-500">by {entry.awarded_by}</span>
                                </div>
                                {entry.notes && (
                                  <p className="mt-2 text-xs text-slate-400 italic">"{entry.notes}"</p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500 text-center py-4">
                            No promotional credits have been awarded to this user yet.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-indigo-500 border-r-transparent border-b-purple-500 border-l-transparent"></div>
                  <p className="mt-3 text-sm text-slate-300">Loading activity data...</p>
                </div>
              )}
            </div>
          )}
          
          {/* Action Buttons - Always visible at bottom */}
          <div className="flex justify-between items-center pt-4 border-t border-slate-800 mt-4 flex-shrink-0">
            {/* Add Promo Credits Button */}
            <Button
              onClick={() => setPromoCreditsModalOpen(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="add-promo-credits-btn"
            >
              <Coins className="w-4 h-4 mr-2" />
              Add Promo Credits
            </Button>
            
            {/* Close Button */}
            <Button
              onClick={() => setActivityModalOpen(false)}
              className="bg-slate-800 text-slate-200 hover:bg-slate-700"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Modal */}
      <Dialog open={archiveModalOpen} onOpenChange={setArchiveModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Archive className="w-5 h-5 text-orange-400" />
              Archive User Account
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This action will deactivate the user's account
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4 mt-4">
              {/* User Info */}
              <div className="p-4 bg-slate-950/50 rounded-lg border border-slate-800">
                <p className="text-sm text-slate-300 mb-2">
                  <span className="font-semibold text-slate-100">User:</span> {selectedUser.email}
                </p>
                <p className="text-sm text-slate-300">
                  <span className="font-semibold text-slate-100">Name:</span>{' '}
                  {selectedUser.first_name && selectedUser.last_name
                    ? `${selectedUser.first_name} ${selectedUser.last_name}`
                    : selectedUser.name || 'N/A'}
                </p>
              </div>

              {/* Archive Details */}
              <div className="p-4 bg-orange-500/10 border border-orange-400/30 rounded-lg">
                <h4 className="font-semibold text-orange-300 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  What happens when you archive?
                </h4>
                <ul className="text-sm text-slate-300 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-orange-400">•</span>
                    <span>User will <strong>not be able to log in</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-400">•</span>
                    <span>All user data will be <strong>preserved</strong> (library, engagement, manuscripts)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-400">•</span>
                    <span>Account can be <strong>unarchived later</strong> to restore access</span>
                  </li>
                </ul>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  onClick={() => {
                    setArchiveModalOpen(false);
                    setSelectedUser(null);
                  }}
                  variant="outline"
                  className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleArchiveUser}
                  className="bg-orange-600 text-white hover:bg-orange-700"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Archive User
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal — Two-Step Flow */}
      <Dialog open={deleteModalOpen} onOpenChange={(open) => { setDeleteModalOpen(open); if (!open) { setDeleteStep(1); setDeleteConfirmEmail(''); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
          {selectedUser && deleteStep === 1 && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-2 text-red-400">
                  <AlertTriangle className="w-5 h-5" />
                  Delete User Account
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  You are about to delete this user account. This action cannot be undone.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                <div className="p-4 bg-slate-950/50 rounded-lg border border-slate-800 space-y-2">
                  <p className="text-sm text-slate-300">
                    <span className="font-semibold text-slate-100">Email:</span> {selectedUser.email}
                  </p>
                  <p className="text-sm text-slate-300">
                    <span className="font-semibold text-slate-100">Name:</span>{' '}
                    {selectedUser.first_name && selectedUser.last_name
                      ? `${selectedUser.first_name} ${selectedUser.last_name}`
                      : selectedUser.name || 'N/A'}
                  </p>
                  <p className="text-sm text-slate-300">
                    <span className="font-semibold text-slate-100">Roles:</span>{' '}
                    {(selectedUser.roles || []).join(', ') || 'N/A'}
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    onClick={() => { setDeleteModalOpen(false); setSelectedUser(null); }}
                    variant="outline"
                    className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
                    data-testid="delete-step1-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => setDeleteStep(2)}
                    className="bg-red-600 text-white hover:bg-red-700"
                    data-testid="delete-step1-continue"
                  >
                    I Understand, Continue
                  </Button>
                </div>
              </div>
            </>
          )}

          {selectedUser && deleteStep === 2 && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-2 text-red-400">
                  <Trash2 className="w-5 h-5" />
                  FINAL WARNING
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  This is irreversible and may break linked data
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                <div className="p-4 bg-red-500/10 border border-red-400/30 rounded-lg">
                  <p className="text-sm text-red-200 font-semibold leading-relaxed">
                    Deleting this user may break publisher profiles, managed author links, and manuscript ownership. Are you absolutely sure?
                  </p>
                </div>

                <div>
                  <label className="text-xs text-slate-400 block mb-1.5">
                    Type <span className="font-mono text-amber-400">{selectedUser.email}</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmEmail}
                    onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                    placeholder={selectedUser.email}
                    data-testid="delete-confirm-email-input"
                    className="w-full px-3 py-2 rounded-lg text-sm bg-slate-950 border border-slate-700 text-slate-200 outline-none focus:border-red-500 font-mono"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    onClick={() => { setDeleteModalOpen(false); setSelectedUser(null); setDeleteStep(1); setDeleteConfirmEmail(''); }}
                    variant="outline"
                    className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
                    data-testid="delete-step2-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteUser}
                    disabled={deleteConfirmEmail !== selectedUser.email}
                    className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid="delete-step2-confirm"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Permanently Delete User
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Promotional Credits Modal */}
      <AddPromoCreditsModal
        isOpen={promoCreditsModalOpen}
        onClose={() => setPromoCreditsModalOpen(false)}
        user={selectedUser}
        adminUser={currentUser}
        onSuccess={async (result) => {
          // Refresh activity data to show new balance
          if (selectedUser) {
            try {
              const token = localStorage.getItem('accessToken');
              const response = await axios.get(
                `${API}/api/admin/users/${selectedUser.id}/activity`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              setActivityData(response.data);
              
              // Also refresh promo history if it was expanded
              if (promoHistoryExpanded) {
                const historyResponse = await axios.get(
                  `${API}/api/admin/users/${selectedUser.id}/promo-credits/history`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                setPromoHistory(historyResponse.data);
              } else {
                // Reset history so it reloads when expanded
                setPromoHistory(null);
              }
              
              notifySuccess(`Awarded ${result.credits_awarded} credits successfully`);
            } catch (error) {
              console.error('Error refreshing activity data:', error);
            }
          }
        }}
      />

      {/* Compose Email Modal */}
      <ComposeEmailModal
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        recipientEmail={emailRecipient.email}
        recipientName={emailRecipient.name}
      />
      </div>
      {/* End of main content wrapper */}

      {/* Mobile Bottom Navigation */}
      <AdminMobileBottomNav />
      
      {/* Universal Footer */}
      <UniversalFooter />
    </div>
  );
};

export default AdminUserManagementPage;
